import { FormEvent, useEffect, useState } from "react";
import { Info } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { useApiMutation, apiFetch } from "@/lib/api";
import { useToast } from "@/hooks/useToast";

const LOCATIONS = ["krasinskiego", "stanczyka", "rydlowka"];

interface Props { periodId: string; onSaved: () => void; onClose?: () => void; }

interface KeysResponse {
  period_id: string;
  location: string;
  keys: string[];
}

export function ManualEntry({ periodId, onSaved, onClose }: Props) {
  const [location, setLocation] = useState(LOCATIONS[0]);
  const [type, setType] = useState("");
  const [amount, setAmount] = useState("");
  const [usage, setUsage] = useState("");
  const [existingKeys, setExistingKeys] = useState<string[]>([]);
  const [keysOpen, setKeysOpen] = useState(false);
  const { mutate, loading } = useApiMutation();
  const { push } = useToast();

  // Re-fetch existing keys whenever location or period changes.
  useEffect(() => {
    let cancelled = false;
    apiFetch<KeysResponse>(`/api/billing/collection/keys/${periodId}/${location}`)
      .then((r) => { if (!cancelled) setExistingKeys(r.keys); })
      .catch(() => { if (!cancelled) setExistingKeys([]); });
    return () => { cancelled = true; };
  }, [periodId, location]);

  const collision = type.trim() !== "" && existingKeys.includes(type.trim());

  async function submit(e: FormEvent) {
    e.preventDefault();
    const [year, month] = periodId.split("-").map(Number);
    try {
      await mutate("/api/billing/collection/correction", "POST", {
        location,
        type: type.trim(),
        value: amount,
        usage: usage || undefined,
        year, month,
      });
      push({ message: `Zapisano ${type.trim()} (${location})`, tone: "success" });
      setType(""); setAmount(""); setUsage("");
      onSaved();
      onClose?.();
      // Refresh local key list.
      apiFetch<KeysResponse>(`/api/billing/collection/keys/${periodId}/${location}`)
        .then((r) => setExistingKeys(r.keys))
        .catch(() => {});
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">
          Klucze już użyte dla <span className="font-mono">{location}</span> · {periodId}:
        </span>
        <button type="button"
                onClick={() => setKeysOpen((o) => !o)}
                className="text-text-muted hover:text-text"
                title="Pokaż istniejące klucze">
          <Info className="size-4" />
        </button>
      </div>

      {keysOpen && (
        <div className="p-2 rounded bg-bg-subtle border border-border text-xs">
          {existingKeys.length === 0 ? (
            <div className="text-text-subtle italic">brak</div>
          ) : (
            <div className="font-mono flex flex-wrap gap-1">
              {existingKeys.map((k) => (
                <span key={k} className="px-1.5 py-0.5 rounded bg-bg border border-border">{k}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="flex flex-col gap-3">
        <Select id="loc" label="Lokal" value={location} onChange={(e) => setLocation(e.target.value)}>
          {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Input id="key" label="Klucz (typ)" placeholder="np. en-el-korekta"
               value={type} onChange={(e) => setType(e.target.value)} />
        {collision && (
          <p className="text-xs text-danger">
            Klucz <span className="font-mono">{type.trim()}</span> już istnieje — wybierz inny (np. dodaj sufiks).
          </p>
        )}
        <Input id="amt" label="Kwota (PLN)" inputMode="decimal"
               value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input id="usg" label="Zużycie (opcjonalne)" inputMode="decimal"
               value={usage} onChange={(e) => setUsage(e.target.value)} />
        <div className="flex justify-end gap-2 pt-1">
          {onClose && (
            <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
          )}
          <Button type="submit" loading={loading}
                  disabled={!type.trim() || !amount || collision}>
            Dodaj
          </Button>
        </div>
      </form>
    </div>
  );
}
